'use client';

import { useLocale } from 'next-intl';
import { useEffect } from 'react';

import { logger } from '@logger';
import { UserStore, useUserStore } from '@utils/user';
import { GOOGLE_ANALYTICS, GOOGLE_ANALYTICS_GLOBAL } from 'enums/gtm';
import { Language } from 'types/lang';

declare let gtag: any;

const enableAnalytics = process.env.NEXT_PUBLIC_ANALYTICS === 'true' ? true : false;

// --- Consent-aware event queue (module-level singleton) ---
// Persists across React component mounts/unmounts within a page session.
// Resets on full page reload — consent is re-evaluated from the inline script flag.
//
// NEXT_PUBLIC_ANALYTICS_QUEUE_EVENTS=true  → queue events until CMP consent (default)
// NEXT_PUBLIC_ANALYTICS_QUEUE_EVENTS=false → fire events immediately without waiting for consent

const queueEvents = process.env.NEXT_PUBLIC_ANALYTICS_QUEUE_EVENTS !== 'false';

const eventQueue: Array<[string, any]> = [];
let consentGranted = !queueEvents;
let listenerInitialized = !queueEvents;

function initConsentListener() {
  if (listenerInitialized || typeof window === 'undefined') return;
  listenerInitialized = true;

  // Returning user: CMP resolved before React hydrated, flag already set in inline script
  if ((window as any).__functionalConsentGranted === true) {
    consentGranted = true;
    flushQueue();
    return;
  }

  // New user: wait for CMP interaction (accept or reject)
  window.addEventListener('gtm-analytics-consent-granted', () => {
    if (consentGranted) return;
    consentGranted = true;
    flushQueue();
  });

  // Fallback: CMP script blocked/failed — handled by onError in scripts.tsx,
  // which dispatches 'gtm-analytics-consent-granted' immediately.

  // Fallback 2: CMP loaded but wall not visible (cosmetic ad blocker hiding #cmpbox,
  // geo-based skip, or other edge case). Treat as "decline" — grant functional
  // analytics only, deny all ad tracking.
  setTimeout(() => {
    if (consentGranted) return;
    const cmpBox = document.getElementById('cmpbox');
    const shouldDecline = !cmpBox || (() => {
      const style = getComputedStyle(cmpBox);
      const opacity = parseFloat(style.opacity);
      const noPointerEvents = style.pointerEvents === 'none';
      return style.display === 'none'
        || style.visibility === 'hidden'
        || opacity === 0
        || (opacity < 0.2 && noPointerEvents)
        || cmpBox.offsetWidth === 0
        || cmpBox.offsetHeight === 0;
    })();
    if (shouldDecline) {
      gtag('consent', 'update', {
        'ad_storage': 'denied',
        'ad_user_data': 'denied',
        'ad_personalization': 'denied',
        'analytics_storage': 'granted',
      });
      // Synthetic cmpEvent so GTM triggers that depend on s26 vendor consent still fire
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'cmpEvent',
        cmpConsentVendors: 's26',
        userChoiceType: 'automatic',
      });
      consentGranted = true;
      flushQueue();
    }
  }, 5000);
}

function flushQueue() {
  if (typeof gtag === 'undefined') return;
  while (eventQueue.length > 0) {
    const [event, params] = eventQueue.shift()!;
    gtag('event', event, params);
  }
}

function sendOrQueue(event: string, params: any) {
  initConsentListener();
  if (consentGranted) {
    if (typeof gtag === 'undefined') {
      logger.error('gtag is undefined');
      return;
    }
    gtag('event', event, params);
  } else {
    eventQueue.push([event, params]);
  }
}

// --- Public API (signatures unchanged) ---

export interface AnalyticsParams {
  [key: string]: string;
}

export function pushSimpleGAEvent(event: string, values: any = {}) {
  if (!enableAnalytics) {
    logger.debug('Analytics is disabled, to enable set environment variable NEXT_PUBLIC_ANALYTICS=true');
    return;
  }
  sendOrQueue(event, { ...values, send_to: GOOGLE_ANALYTICS_GLOBAL });
}

export function pushGAEvent(userStore: UserStore, event: string, locale: string, values: any = {}) {
  if (!enableAnalytics) {
    logger.debug('Analytics is disabled, to enable set environment variable NEXT_PUBLIC_ANALYTICS=true');
    return;
  }

  const gaLocale = GOOGLE_ANALYTICS.get(locale as Language);

  const valuesWithUserInfo = {
    ...values,
    locale,
    ...userStore.getUserInfoForAnalytics(),
  };

  sendOrQueue(event, { ...valuesWithUserInfo, send_to: GOOGLE_ANALYTICS_GLOBAL });
  sendOrQueue(event, { ...valuesWithUserInfo, send_to: gaLocale });
}

interface GAProps {
  event: string;
  id?: string | number;
  values: any;
}

export default function GAScript({ event, values }: GAProps) {
  const userStore = useUserStore();
  const locale = useLocale();

  useEffect(() => {
    if (userStore.isReady) {
      pushGAEvent(userStore, event, locale, values);
    }
  }, [userStore.isReady]);

  return <></>;
}
